<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import { useAccount } from 'dashboard/composables/useAccount';
import CaptainUsage from 'dashboard/api/captain/usage';

import BillingMeter from 'dashboard/routes/dashboard/settings/billing/components/BillingMeter.vue';

const props = defineProps({
  // The overview's shared RangeSelector value: '7' | '30' | '90' |
  // 'this_month' | 'last_month'. Converted to a since/until window below.
  range: {
    type: String,
    default: '30',
  },
});

const { t } = useI18n();
const { currentAccount } = useAccount();

const usage = ref(null);
const isLoading = ref(false);

// Increments on every fetch so a slow response for a superseded range can't
// clobber the latest request's state (mirrors the overview's metrics fetch).
let fetchToken = 0;
let abortController = null;

// Convert a RangeSelector key into an absolute [since, until] window. Day ranges
// are inclusive of today; month ranges anchor to calendar boundaries.
const rangeToWindow = range => {
  const now = new Date();
  if (range === 'this_month') {
    return { since: startOfMonth(now), until: now };
  }
  if (range === 'last_month') {
    const previous = subMonths(now, 1);
    return { since: startOfMonth(previous), until: endOfMonth(previous) };
  }
  const days = Number(range) || 30;
  return { since: startOfDay(subDays(now, days - 1)), until: endOfDay(now) };
};

const fetchUsage = async () => {
  fetchToken += 1;
  const token = fetchToken;
  abortController?.abort();
  abortController = new AbortController();
  const { signal } = abortController;
  isLoading.value = true;

  const { since, until } = rangeToWindow(props.range);

  let data = null;
  try {
    ({ data } = await CaptainUsage.getUsage({
      since: since.toISOString(),
      until: until.toISOString(),
      signal,
    }));
  } catch {
    data = null;
  }

  if (token !== fetchToken || signal.aborted) return;
  usage.value = data;
  isLoading.value = false;
};

watch(() => props.range, fetchUsage, { immediate: true });

onUnmounted(() => {
  abortController?.abort();
});

const totals = computed(
  () =>
    usage.value?.totals ?? {
      credits_used: 0,
      events: 0,
      input_tokens: 0,
      output_tokens: 0,
    }
);

// Truly empty only when nothing was recorded — zero-cost opt-out events still
// count as usage worth showing, so we key off event count, not credits.
const isEmpty = computed(() => !isLoading.value && totals.value.events === 0);

const formatNumber = value =>
  Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const totalTiles = computed(() => [
  {
    key: 'credits',
    label: t('CAPTAIN.OVERVIEW.USAGE.TOTALS.CREDITS'),
    value: formatNumber(totals.value.credits_used),
  },
  {
    key: 'events',
    label: t('CAPTAIN.OVERVIEW.USAGE.TOTALS.EVENTS'),
    value: formatNumber(totals.value.events),
  },
  {
    key: 'inputTokens',
    label: t('CAPTAIN.OVERVIEW.USAGE.TOTALS.INPUT_TOKENS'),
    value: formatNumber(totals.value.input_tokens),
  },
  {
    key: 'outputTokens',
    label: t('CAPTAIN.OVERVIEW.USAGE.TOTALS.OUTPUT_TOKENS'),
    value: formatNumber(totals.value.output_tokens),
  },
]);

// The account credit balance rides on the account payload; the meter only shows
// a bar when the plan actually caps credits, otherwise the consumed-only line.
const credits = computed(() => currentAccount.value?.ai_credits);
const hasCreditQuota = computed(
  () => !!currentAccount.value?.plan_limits?.ai_credits
);
</script>

<template>
  <div class="border rounded-xl bg-n-solid-1 border-n-weak">
    <div
      class="flex items-center justify-between px-5 py-3 border-b border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('CAPTAIN.OVERVIEW.USAGE.TITLE') }}
      </span>
      <span class="text-xs text-n-slate-10">
        {{ $t('CAPTAIN.OVERVIEW.USAGE.SUBTITLE') }}
      </span>
    </div>

    <div
      v-if="isLoading"
      class="p-5"
      role="status"
      aria-busy="true"
      :aria-label="$t('CAPTAIN.OVERVIEW.USAGE.LOADING')"
    >
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div
          v-for="tile in totalTiles"
          :key="tile.key"
          class="h-16 rounded-xl bg-n-slate-3 animate-pulse"
        />
      </div>
    </div>

    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center gap-2 px-5 py-10 text-center"
    >
      <span class="i-lucide-gauge size-6 text-n-slate-9" />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('CAPTAIN.OVERVIEW.USAGE.EMPTY') }}
      </p>
    </div>

    <div v-else class="flex flex-col gap-6 px-5 pt-4 pb-5">
      <!-- Totals -->
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div
          v-for="tile in totalTiles"
          :key="tile.key"
          class="flex flex-col gap-1 p-3 border rounded-xl border-n-weak"
        >
          <span class="text-xs text-n-slate-11">{{ tile.label }}</span>
          <span class="text-lg font-semibold tabular-nums text-n-slate-12">
            {{ tile.value }}
          </span>
        </div>
      </div>

      <!-- Credit balance -->
      <div v-if="credits">
        <BillingMeter
          v-if="hasCreditQuota"
          :title="$t('CAPTAIN.OVERVIEW.USAGE.BALANCE.TITLE')"
          :consumed="credits.consumed"
          :total-count="credits.total_count"
        />
        <div
          v-else
          class="flex items-center justify-between gap-5 text-xs uppercase text-n-slate-10"
        >
          <div class="font-medium tracking-wider">
            {{ $t('CAPTAIN.OVERVIEW.USAGE.BALANCE.TITLE') }}
          </div>
          <div class="tabular-nums">
            {{
              $t('CAPTAIN.OVERVIEW.USAGE.BALANCE.UNLIMITED', {
                consumed: formatNumber(credits.consumed),
              })
            }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
